"use client";

import { useCallback, useState, type ChangeEvent, type KeyboardEvent, type Ref } from "react";
import { cleanCode } from "@/lib/live/code-entry";

type CodeInputProps = {
  /** Ce qui est tapé, déjà nettoyé : rien que des lettres et des chiffres. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  /**
   * Le dernier caractère vient d'arriver. Appelé une fois par code complété,
   * pas à chaque rendu d'un code déjà complet : retaper un caractère après une
   * erreur relance, rester sur un code refusé ne relance rien.
   */
  readonly onComplete?: (value: string) => void;
  /** Le nombre de cases. Six pour les codes du live. */
  readonly length?: number;
  /** Le code a été refusé : les traits le disent tous ensemble. */
  readonly invalid?: boolean;
  /** Une vérification est en cours : les cases s'estompent, la saisie reste. */
  readonly busy?: boolean;
  readonly ariaLabel: string;
  readonly inputRef?: Ref<HTMLInputElement>;
};

/**
 * Un code à recopier, en cases : une par caractère.
 *
 * # Six cases, un seul champ
 *
 * Ce qu'on voit, ce sont les cases ; ce qui reçoit la frappe, c'est un seul
 * `<input>` transparent posé par-dessus. Six champs séparés, qu'il faudrait
 * faire avancer à chaque touche et reculer à chaque effacement, cassent
 * exactement ce que ce composant doit réussir : coller le code entier reçu par
 * message, le clavier d'un téléphone qui propose ses suggestions, un lecteur
 * d'écran qui doit annoncer *un* champ et sa valeur. Le champ unique fait tout
 * ça nativement ; les cases ne font que le dessiner.
 *
 * Le curseur reste au bout : les flèches et la sélection sont neutralisées, car
 * un curseur invisible posé au milieu du code ferait écrire là où rien ne le
 * montre.
 *
 * # Sur un trait, comme les autres champs du produit
 *
 * Chaque case est un trait, pas une boîte : c'est le dessin de l'écran de
 * compte et de celui du code d'un récital. La case où tombera le prochain
 * caractère a son trait en violet et un curseur ; un code refusé passe tous les
 * traits au rouge.
 *
 * La casse n'est jamais touchée — les codes du live font de `a` et de `A` deux
 * caractères différents —, et rien ne corrige ni ne complète.
 */
export function CodeInput({
  value,
  onChange,
  onComplete,
  length = 6,
  invalid = false,
  busy = false,
  ariaLabel,
  inputRef,
}: CodeInputProps) {
  const [focused, setFocused] = useState(false);

  // Le champ vient de l'événement : pas de ref à soi, celle de l'appelant passe
  // telle quelle.
  const toEnd = useCallback((input: HTMLInputElement) => {
    const end = input.value.length;

    if (input.selectionStart !== end || input.selectionEnd !== end) {
      input.setSelectionRange(end, end);
    }
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = cleanCode(event.target.value, length);

      onChange(next);

      if (next.length === length && next !== value) {
        onComplete?.(next);
      }
    },
    [length, onChange, onComplete, value],
  );

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowRight" || event.key === "Home" || event.key === "End") {
      event.preventDefault();
    }
  }, []);

  // La case qui attend : la suivante, ou la dernière quand le code est complet.
  const current = Math.min(value.length, length - 1);

  return (
    <div className={`relative w-fit transition-opacity duration-150 ${busy ? "opacity-60" : ""}`}>
      {/*
        `account-underline-input` : l'anneau global de `:focus-visible` est hors
        `@layer`, aucune utilitaire ne le retire (voir `globals.css`). Le trait
        de la case courante dit le focus.

        16 px et non la taille du dessin : en dessous, Safari iOS zoome la page
        au focus. Le texte est transparent de toute façon.

        Pas de `maxLength` : il couperait le texte **avant** le nettoyage, et un
        code collé avec ses séparateurs (« Qr 7-tU2 ») perdrait sa fin. La
        longueur est bornée par `cleanCode`.
      */}
      <input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={(event) => {
          setFocused(true);
          toEnd(event.currentTarget);
        }}
        onBlur={() => setFocused(false)}
        onSelect={(event) => toEnd(event.currentTarget)}
        onClick={(event) => toEnd(event.currentTarget)}
        aria-label={ariaLabel}
        aria-invalid={invalid}
        inputMode="text"
        autoCapitalize="off"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
        className="account-underline-input absolute inset-0 h-full w-full cursor-text bg-transparent text-[16px] text-transparent caret-transparent outline-none selection:bg-transparent"
      />

      <div aria-hidden className="pointer-events-none flex gap-1.5">
        {Array.from({ length }, (_, index) => {
          const char = value[index] ?? "";
          const waiting = focused && index === current;

          return (
            <span
              key={index}
              className={`relative grid h-10 w-[26px] place-items-center border-b font-mono text-[18px] text-text-primary transition-colors duration-150 ${
                invalid
                  ? "border-red-400/60"
                  : waiting
                    ? "border-accent"
                    : char !== ""
                      ? "border-white/30"
                      : "border-border-strong"
              }`}
            >
              {char}

              {waiting && char === "" && (
                <span className="absolute h-5 w-px bg-accent-bright motion-safe:animate-pulse" />
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
